import { Injectable } from '@angular/core';
import { DropdownItem } from '@frk/eds-components';
import { PremiumDiscountAnalysisPeriod } from '@models';
import { MapperFactory } from '@products/utils/mappers/mapper-factory';
import { PremiumDiscountMapper } from '@products/utils/mappers/premium-discount.type.mapper';
import { MapperParams } from '@products/utils/mappers/type.mapper';
import { SiteConfigService } from '@services/site-config.service';
import { TranslateService } from '@shared/translate/translate.service';
import { PremiumDiscountDTO } from '@types';
import { Logger } from '@utils/logger';
import { Apollo } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  FundDataServiceParams,
  GraphQLFundDataService,
} from './graphql-fund-data.service';

const logger = Logger.getLogger('PremiumDiscountDataService');

interface SortedPeriod extends DropdownItem {
  order: string;
}

dayjs.extend(advancedFormat);

@Injectable({
  providedIn: 'root',
})
export class PremiumDiscountService extends GraphQLFundDataService {
  constructor(
    apollo: Apollo,
    siteConfigService: SiteConfigService,
    private mapperFactory: MapperFactory,
    private translateService: TranslateService
  ) {
    super(apollo, siteConfigService);
  }

  public register(
    query: DocumentNode,
    fundDataServiceParams: FundDataServiceParams
  ): Observable<PremiumDiscountAnalysisPeriod[]> {
    const periodDates: DropdownItem[] = this.getPeriods();
    logger.debug('periods', periodDates);
    const periodObservables: Array<
      Observable<PremiumDiscountAnalysisPeriod>
    > = periodDates.map(
      (item: DropdownItem): Observable<PremiumDiscountAnalysisPeriod> =>
        super
          .register(query, this.getVariables(fundDataServiceParams, item.value))
          .pipe(
            map((rawPremiumDiscountdata) => {
              const premiumDiscountDTO: PremiumDiscountDTO[] =
                rawPremiumDiscountdata?.data?.PremiumDiscount
                  ?.premiumdiscount || null;

              const mappperParams: MapperParams = {
                config: this.siteConfigService,
                translateService: this.translateService,
                includeSoftClosed: true,
              };

              const mappedPremiumDiscount: PremiumDiscountAnalysisPeriod = this.mapperFactory
                .createMapper(PremiumDiscountMapper, mappperParams)
                .toDomain(premiumDiscountDTO);

              // return null if no data for period
              if (!mappedPremiumDiscount) {
                return null;
              }

              mappedPremiumDiscount.label = item.label;
              mappedPremiumDiscount.value = item.value;

              return mappedPremiumDiscount;
            })
          )
    );
    return forkJoin(periodObservables);
  }

  private getPeriods = (): DropdownItem[] => {
    const now = dayjs();
    // start by defining previous 8 quarters
    const periods: SortedPeriod[] = [1, 2, 3, 4, 5, 6, 7, 8].map(
      (i): SortedPeriod => {
        const qtrEnd = now.clone().subtract(i, 'quarter').endOf('quarter');
        return {
          label: qtrEnd.format('[Q]Q YYYY'),
          value: parseInt(qtrEnd.format('YYYYMMDD'), 10),
          order: qtrEnd.format('YYYYMMDD') + '1',
        };
      }
    );

    // add previous 2 full calendar years
    [1, 2].forEach((i) => {
      const yearStart = now.clone().subtract(i, 'year').startOf('year');
      periods.push({
        label: `${this.translateService.instant(
          'products.calendar-year'
        )} ${yearStart.format('YYYY')}`,
        value: parseInt(yearStart.format('YYYYMMDD'), 10),
        order: yearStart.endOf('year').format('YYYYMMDD') + '2',
      });
    });

    return periods.sort(
      (a: SortedPeriod, b: SortedPeriod): number =>
        parseInt(b.order, 10) - parseInt(a.order, 10)
    );
  };

  /**
   * Return required variables value to graphql query.
   */
  private getVariables(
    fundDataServiceParams: FundDataServiceParams,
    asofdate: number
  ): Record<string, any> {
    return {
      countrycode: this.getCountry(),
      languagecode: this.getLanguage(),
      fundid: fundDataServiceParams.fundId,
      shareclasscode: fundDataServiceParams.shareClassCode,
      asofdate,
    };
  }
}
